import React, { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Lock } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";

import PasswordRulesChecklist from "@/src/components/form-fields/PasswordRulesChecklist";
import { Button } from "@/src/components/ui/button";

import LoginField from "./LoginField";
import { createNewPasswordSchema } from "./loginSchemas";
import LoginTitle from "./LoginTitle";
import AdminUserAPI from "../../api/AdminUserAPI";
import history from "../common/navigation/AppHistory";
import { PROFILE_ROUTE } from "../routes/routes";

import type { JSX } from "react";

import type { NewPasswordFormValues } from "./loginSchemas";
import type { AppState } from "../../reducers";

interface NewPasswordProps {
  redirectTo: string;
}

function NewPassword({ redirectTo }: NewPasswordProps): JSX.Element {
  const { t } = useTranslation("login");

  const isLoggedIn = useSelector(
    (state: AppState) => state.loginReducer.isLoggedIn,
  );

  const { search } = useLocation();
  const resetToken = useMemo(
    () => new URLSearchParams(search).get("reset_token") ?? "",
    [search],
  );

  const schema = useMemo(() => createNewPasswordSchema(t), [t]);
  const methods = useForm<NewPasswordFormValues>({
    defaultValues: { password: "" },
    mode: "onBlur",
    resolver: zodResolver(schema),
  });

  const { isSubmitting } = methods.formState;
  const password = methods.watch("password");

  useEffect(() => {
    if (!resetToken) {
      history.push("/");
      return;
    }

    if (isLoggedIn) {
      history.push(PROFILE_ROUTE);
    }
  }, [isLoggedIn, resetToken]);

  const onValid = async ({
    password: newPassword,
  }: NewPasswordFormValues): Promise<void> => {
    try {
      await AdminUserAPI.setNewPassword({ password: newPassword, resetToken });

      toast.success(t("successNewPassword"));
      history.push(redirectTo);
    } catch (error) {
      toast.error(t("genericError", { ns: "error" }));
      console.error("Error while setting the new password:", error);
    }
  };

  return (
    <React.Fragment>
      <LoginTitle>{t("newPasswordTitle")}</LoginTitle>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <form
          noValidate
          className="flex w-full flex-col gap-5"
          onSubmit={methods.handleSubmit(onValid)}
        >
          <LoginField
            withVisibilityToggle
            autoComplete="new-password"
            icon={Lock}
            label={t("passwordLabel")}
            name="password"
            placeholder={t("passwordContent")}
            type="password"
          />

          <PasswordRulesChecklist password={password} />

          <Button
            className="mt-3 w-full"
            disabled={isSubmitting}
            size="lg"
            type="submit"
          >
            {t("resetButtonLabel")}
          </Button>
        </form>
      </FormProvider>
    </React.Fragment>
  );
}

export default NewPassword;
